import { queryOptions, skipToken, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { audienceKeys, parseAudience, type Audience } from './entity';

const STALE_TIME = 60_000;

/**
 * Workspace audiences (unpaginated — the endpoint takes no query params). Each
 * row is sideloaded into its detail slot so a follow-up detail read resolves
 * from cache.
 *
 * @public
 */
export async function getAudiences(workspaceId: number, qc: QueryClient): Promise<Audience[]> {
  const raw = await hydraUiApi.audiences.list({ params: { path: { environment_id: String(workspaceId) } } });
  const audiences = (raw.audiences ?? []).map(parseAudience);
  for (const item of audiences) {
    qc.setQueryData(audienceKeys.detail(workspaceId, item.id), item);
  }
  return audiences;
}

/** @public */
export const getAudiencesQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: audienceKeys.list(workspaceId, undefined),
    queryFn: () => getAudiences(workspaceId, qc),
    staleTime: STALE_TIME,
  });

/** @public */
export const useAudiences = ({ queryConfig }: { queryConfig?: QueryConfig<typeof getAudiencesQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useQuery({ ...getAudiencesQueryOptions(workspaceId, qc), ...queryConfig });
};

/** @public */
export async function getAudience(workspaceId: number, audienceId: string, qc: QueryClient): Promise<Audience> {
  const raw = await hydraUiApi.audiences.get({
    params: { path: { environment_id: String(workspaceId), id: audienceId } },
  });
  if (raw.audience == null) {
    throw new Error(`audience ${audienceId} not found`);
  }
  const parsed = parseAudience(raw.audience);
  qc.setQueryData(audienceKeys.detail(workspaceId, parsed.id), parsed);
  return parsed;
}

/** @public */
export const useAudienceOptional = ({
  workspaceId,
  id,
  enabled = true,
}: {
  workspaceId: number;
  id: string;
  enabled?: boolean;
}) => {
  const qc = useQueryClient();
  const active = enabled && workspaceId > 0 && id !== '';
  return useQuery({
    queryKey: active ? audienceKeys.detail(workspaceId, id) : ['audience', 'inactive'],
    queryFn: active ? () => getAudience(workspaceId, id, qc) : skipToken,
    staleTime: STALE_TIME,
    retry: false,
  });
};
